import { StateSetter, useState } from 'react';

import { mutations } from 'entities';

import { Alert, Box, Button, Column, Input, LabeledCheckbox } from 'components/Blocks';
import { MessageCircle } from 'components/Blocks/Icons';
import { PrivacyPolicyLink, UseTermsLink } from 'components/Links';

import { useUser } from 'providers/Auth';
import { useToggleV2 } from 'hooks';

import { phoneByCountry } from './utils';
import { onlyNumericChars } from 'utils/text';
import { useLang } from 'locale';
import { env } from 'config/env';
import { cn } from 'styles';

interface PhoneFormProps {
  wpp: string;
  setWpp: StateSetter<string>;
  onSent: () => void;
  maskedPhone: string;
}

export function PhoneForm({ setWpp, wpp, onSent, maskedPhone }: PhoneFormProps) {
  const { t } = useLang();
  const { country } = useUser();

  const [termsOk, termsHandle] = useToggleV2();

  const [blockedPhones, setBlockedPhones] = useState([] as string[]);

  const phoneConfig = phoneByCountry[country];

  const maxLength =
    typeof phoneConfig.length === 'number' ? phoneConfig.length : phoneConfig.length.max;

  const minLength =
    typeof phoneConfig.length === 'number' ? phoneConfig.length : phoneConfig.length.min;

  const handleWppChange = (value: string) => {
    const clean = onlyNumericChars(value);

    if (clean.length > maxLength) return;

    setWpp(clean);
  };

  const validPhone = wpp.length >= minLength && wpp.length <= maxLength;

  const canSendCode = validPhone && termsOk;

  const isBlocked = blockedPhones.includes(wpp);

  const sendCode = mutations.whatsapp.sendConfirmationCode.use({
    block: !canSendCode || isBlocked,

    params: {
      phone: onlyNumericChars(`${phoneConfig.code}${wpp}`),
    },

    onSuccess: onSent,

    onError: ({ code }) => {
      if (code === 'PHONE_BLOCKED') setBlockedPhones((old) => [...old, wpp]);
    },
  });

  return (
    <Column gap="6">
      <Box
        className="mx-auto w-fit flex-grow-0 bg-primary p-3 text-primary-foreground"
        border="circle"
      >
        <MessageCircle size={24} />
      </Box>

      <h2 className="text-center text-xl font-semibold text-primary">{t('verify-whatsapp')}</h2>

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs">{phoneConfig.code}</span>

        <Input
          className={cn('pl-12', isBlocked && 'border-destructive')}
          placeholder={phoneConfig.placeholder}
          size="large"
          value={maskedPhone}
          onValueChange={handleWppChange}
        />
      </div>

      {phoneConfig.helpMessage && (
        <small className="-mb-1 -mt-4 pl-1 text-xs italic text-foreground-light">
          {phoneConfig.helpMessage}
        </small>
      )}

      {isBlocked && (
        <Alert level="warning" type="lightly-filled" className="max-w-80">
          {t('phone-blocked-disclaimer')}{' '}
          <a
            href={env.supportUrl}
            target="support"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            {t('support')}
          </a>
          .
        </Alert>
      )}

      <LabeledCheckbox
        labelClassName="font-normal"
        checked={termsOk}
        onValueChange={termsHandle.toggle}
        label={
          <span className="max-w-72 text-xs text-foreground-light">
            {t('terms-agreement-start')}{' '}
            <UseTermsLink className="text-foreground hover:underline" /> {t('and-the')}{' '}
            <PrivacyPolicyLink className="text-foreground hover:underline" />
          </span>
        }
      />

      <Button className="w-full" disabled={!canSendCode || isBlocked} {...sendCode.buttonProps}>
        <MessageCircle />
        {t('send-code')}
      </Button>
    </Column>
  );
}
